import type { AutocompleteRenderOptionState } from '@mui/material/Autocomplete';
import {
  useEffect,
  useMemo,
  useState,
  type HTMLAttributes,
  type Key,
  type ReactNode,
} from 'react';
import type { SxProps, Theme } from '@mui/material/styles';

import { type ShortUser, useUserAutocompleteChoices } from '@src/features/users';
import Avatar from '../Avatar';
import { Box } from '../layout';
import { ListItem } from '../List';
import Typography from '../Typography';
import AsyncAutocompleteInput from './AsyncAutocompleteInput';
import type { InputProps, TextFieldProps } from './TextInput';

type BackendFilterValue = string | number | boolean | null | undefined;

export type UserAutocompleteInputProps = {
  value?: ShortUser | null;
  onChange: (value: ShortUser | null) => void;
  onBlur?: (event: unknown) => void;
  backendFilters?: Record<string, BackendFilterValue>;
  disabled?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  highlightSearchQuery?: boolean;
  InputProps?: InputProps;
  InputLabelProps?: TextFieldProps['InputLabelProps'];
  label?: ReactNode;
  pageSize?: number;
  placeholder?: string;
  required?: boolean;
  showPortrait?: boolean;
  sx?: SxProps<Theme>;
};

const styles = {
  option: {
    alignItems: 'center',
    display: 'flex',
    gap: 1,
    minWidth: 0,
  },
  optionAvatar: {
    flex: '0 0 auto',
    height: 32,
    width: 32,
  },
  optionLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  optionLabelHighlighted: {
    color: 'text.secondary',
  },
  optionLabelMatch: {
    color: 'text.primary',
  },
  field: (showPortrait: boolean) => (
    showPortrait ? {
      '& .MuiInputBase-root': {
        height: 32,
        py: 0,
        overflow: 'hidden',
      },
      '& .MuiAutocomplete-inputRoot': {
        flexWrap: 'nowrap',
        gap: 1,
      },
      '& .MuiInputAdornment-root': {
        flex: '0 0 auto',
        m: 0,
      },
      '& .MuiAutocomplete-input': {
        minWidth: 0,
        py: 0,
      },
    } : {}),
  adornment: {
    display: 'flex',
  },
  listOption: (showPortrait: boolean) => (
    showPortrait ? {
      minHeight: 40,
      py: 0.5,
    } : {}),
} as const;

function getUserOptionLabel(user: ShortUser) {
  return [user.firstName, user.familyName].join(' ').trim() || user.initials || user.email;
}

function normalizeBackendFilters(
  filters: Record<string, BackendFilterValue> | undefined,
) {
  const queryParams: Record<string, string> = {};
  Object.entries(filters ?? {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      return;
    }
    queryParams[key] = String(value);
  });
  return queryParams;
}

type LabelWord = {
  fieldPriority: number;
  text: string;
};

function getLabelWords(option: ShortUser): LabelWord[] {
  const nameFields = [
    { fieldPriority: 1, value: option.firstName },
    { fieldPriority: 0, value: option.familyName },
  ].filter(({ value }) => value.trim());
  const fields = nameFields.length ? nameFields : [
    { fieldPriority: 2, value: option.initials || option.email },
  ];

  return fields.flatMap(({ fieldPriority, value }) => (
    value.trim().split(/\s+/).map((text) => ({ fieldPriority, text }))
  ));
}

function HighlightedWord({
  match,
  word,
}: {
  match?: { index: number, length: number },
  word: string,
}) {
  if (!match) {
    return word;
  }

  const matchEnd = match.index + match.length;
  return (
    <>
      {word.slice(0, match.index)}
      <Box component="span" sx={styles.optionLabelMatch}>
        {word.slice(match.index, matchEnd)}
      </Box>
      {word.slice(matchEnd)}
    </>
  );
}

function HighlightedOptionLabel({ option, query }: { option: ShortUser, query: string }) {
  const terms = [...new Set(query.trim().toLowerCase().split(/\s+/).filter(Boolean))];
  const words = getLabelWords(option);
  const matchesByWord = new Map<number, { index: number, length: number }>();
  const matchedTerms = new Set<number>();

  const candidates = terms.flatMap((term, termIndex) => (
    words.flatMap(({ fieldPriority, text }, wordIndex) => {
        const index = text.toLowerCase().indexOf(term);
        return index === -1 ? [] : [{
          fieldPriority,
          index,
          length: term.length,
          termIndex,
          wordIndex,
        }];
      })
  )).sort((first, second) => (
    Number(first.index !== 0) - Number(second.index !== 0)
    || first.fieldPriority - second.fieldPriority
    || first.termIndex - second.termIndex
    || first.index - second.index
    || first.wordIndex - second.wordIndex
  ));

  candidates.forEach(({ index, length, termIndex, wordIndex }) => {
    if (!matchedTerms.has(termIndex) && !matchesByWord.has(wordIndex)) {
      matchedTerms.add(termIndex);
      matchesByWord.set(wordIndex, { index, length });
    }
  });

  if (!terms.length) {
    return getUserOptionLabel(option);
  }

  return words.map(({ text }, index) => (
    <Box component="span" key={`${index}-${text}`}>
      {index > 0 && ' '}
      <HighlightedWord match={matchesByWord.get(index)} word={text} />
    </Box>
  ));
}

function UserOptionContent({
  highlightSearchQuery,
  option,
  query,
  showPortrait,
}: {
  highlightSearchQuery: boolean,
  option: ShortUser,
  query: string,
  showPortrait: boolean,
}) {
  const label = getUserOptionLabel(option);
  return (
    <Box sx={styles.option}>
      {showPortrait && (
        <Avatar src={option.portrait} sx={styles.optionAvatar} />
      )}
      <Typography
        component="span"
        sx={[
          styles.optionLabel,
          ...(highlightSearchQuery ? [styles.optionLabelHighlighted] : []),
        ]}
      >
        {highlightSearchQuery ? (
          <HighlightedOptionLabel option={option} query={query} />
        ) : label}
      </Typography>
    </Box>
  );
}

function renderUserOption(showPortrait: boolean, highlightSearchQuery: boolean) {
  return (
    props: HTMLAttributes<HTMLLIElement> & { key: Key },
    option: ShortUser,
    state: AutocompleteRenderOptionState,
  ) => {
    const { key, ...restProps } = props;
    return (
      <ListItem key={key} {...restProps} sx={styles.listOption(showPortrait)}>
        <UserOptionContent
          highlightSearchQuery={highlightSearchQuery}
          option={option}
          query={state.inputValue}
          showPortrait={showPortrait}
        />
      </ListItem>
    );
  }
}

function UserAutocompleteInput({
  backendFilters,
  disabled,
  error,
  helperText,
  highlightSearchQuery = true,
  InputProps,
  InputLabelProps,
  label,
  showPortrait = true,
  onBlur,
  onChange,
  pageSize = 50,
  placeholder = '',
  required,
  sx,
  value = null,
}: UserAutocompleteInputProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const resetSearchQuery = () => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
  };

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [searchQuery]);

  const queryParams = useMemo(
    () => ({
      ...normalizeBackendFilters(backendFilters),
      page_size: String(pageSize),
      ...(debouncedSearchQuery ? { q: debouncedSearchQuery } : {}),
    }),
    [backendFilters, debouncedSearchQuery, pageSize],
  );

  const usersQuery = useUserAutocompleteChoices({
    enabled: !disabled,
    queryParams,
  });

  return (
    <AsyncAutocompleteInput<ShortUser>
      disabled={disabled}
      error={error}
      filterOptions={(options) => options}
      getOptionLabel={getUserOptionLabel}
      helperText={helperText}
      InputProps={{
        ...InputProps,
        startAdornment: value && showPortrait ? (
          <Box sx={styles.adornment}>
            <Avatar src={value.portrait} sx={styles.optionAvatar} />
          </Box>
        ) : InputProps?.startAdornment,
      }}
      InputLabelProps={InputLabelProps}
      isOptionEqualToValue={(option, selectedValue) =>
        option.id === selectedValue.id
      }
      label={label}
      loading={usersQuery.isFetching}
      onBlur={(event) => {
        resetSearchQuery();
        onBlur?.(event);
      }}
      onChange={(nextValue) => {
        resetSearchQuery();
        onChange(nextValue);
      }}
      onInputChange={(_event, nextInputValue, reason) => {
        if (reason === 'input') {
          setSearchQuery(nextInputValue);
        }
        if (reason === 'clear' || reason === 'blur') {
          resetSearchQuery();
        }
      }}
      options={usersQuery.data?.results ?? []}
      placeholder={placeholder}
      renderOption={renderUserOption(showPortrait, highlightSearchQuery)}
      required={required}
      sx={[styles.field(showPortrait), ...(Array.isArray(sx) ? sx : [sx])]}
      value={value}
    />
  );
}

UserAutocompleteInput.displayName = 'UserAutocompleteInput';

export default UserAutocompleteInput;
